import {Component, Inject, OnInit, ViewChild, AfterViewInit, OnDestroy} from '@angular/core';
import {ConfirmationService, LazyLoadEvent, MessageService} from "primeng/api";
import {MAT_DIALOG_DATA, MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {ImportExportComponent} from "@app/_components/_helpers/import-export/import-export.component";
import {User} from "@app/_models";
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {AlertService, AuthenticationService, ConstantService} from "@app/_services";
import {ReservationService} from "@app/_components/reservation";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {PropertyService} from "@app/_components/property";
import {Location} from "@angular/common";
import * as moment from "moment";
import { first } from 'rxjs/operators';
import {Table} from "primeng/table";
import { MultiSelect } from 'primeng/multiselect';
import {ReservationContainerComponent} from "@app/_components/reservation/components/reservation/reservation-container/reservation-container.component";
import {ReservationAdvanceSearchComponent} from "@app/_components/reservation/components/reservation-advance-search/reservation-advance-search.component";
import {ReservationCreateComponent} from "@app/_components/reservation/components/reservation-create/reservation-create.component";
import {StatementComponent} from "@app/_components/owner-statements/statement/statement.component";
import {OwnerStmtService} from "@app/_components/owner-statements/owner-stmt.service";
import {StatementCreateComponent} from "@app/_components/owner-statements/statement-create/statement-create.component";
import { StatementsComponent } from '@app/_components/owner-stmt-view/statements/statements.component';
import { OwnerStatementMailService } from './owner-statement-mail.service';
import { TopicMessagesViewComponent } from '@app/_components/_shared/topic-messages-view/topic-messages-view.component';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';

@Component({
    selector: 'app-owner-statements',
    templateUrl: './owner-statements.component.html',
    styleUrls: ['./owner-statements.component.scss'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    standalone: false
})
export class OwnerStatementsComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;
  @ViewChild('pt') table: Table;
  @ViewChild('locationMultiSelect') locationMultiSelect: MultiSelect;

  datasource: any[];
  reservation: User;
  reservations: any[] = [];
  recordTotal: number = 0;
  lazyLoadEvent: LazyLoadEvent;
  loading = true;
  multiSortMeta = [];

  /**
   * Footer totals across whatever rows are currently rendered on the page
   * (after the active filter/sort). Page size defaults to 300; most realistic
   * filtered views (PC=41, full HI=221, full April=238) fit on one page, so
   * these totals effectively reflect the entire filtered set in practice.
   *
   * Recomputed via a getter (cheap — 200-300 rows × ~15 fields). Returns null
   * when no rows are loaded so the footer template renders nothing.
   */
  get pageTotals(): any | null {
    const rows = this.reservations;
    if (!rows || rows.length === 0) return null;
    const t: any = {
      rowCount: rows.length,
      paidNights: 0, occupancyRateSum: 0, guestNight: 0, ownerNights: 0, ownerGuestNights: 0, maintenanceNights: 0,
      rentalRevenue: 0, paymentToOwner: 0,
      maintenanceAndHousekeepingTotal: 0, maintenanceAndHousekeepingPaidByKbmTotal: 0, maintenanceAndHousekeepingPaidByOwnerTotal: 0,
      mgntExpense: 0, maintenancePaidByOwnerTotal: 0, housekeepingPaidByOwnerTotal: 0, taxExpense: 0, totalExpense: 0,
      startingBalance: 0, endingBalance: 0,
      discrepancyAmount: 0, matchCount: 0, mismatchCount: 0,
      maintenanceCreditTotal: 0, housekeepingCreditTotal: 0,
    };
    for (const r of rows) {
      const s = r?.stmtSummeryAPI || {};
      t.paidNights        += +(s.paidNights        || 0);
      t.occupancyRateSum  += +(s.occupancyRate     || 0);
      t.guestNight        += +(r?.guestNight       || 0);
      t.ownerNights       += +(r?.ownerNights      || 0);
      t.ownerGuestNights  += +(r?.ownerGuestNights || 0);
      t.maintenanceNights += +(r?.maintenanceNights || 0);
      t.rentalRevenue     += +(s.rentalRevenue     || 0);
      t.paymentToOwner    += +(s.paymentToOwner    || 0);
      t.maintenanceAndHousekeepingTotal             += +(s.maintenanceAndHousekeepingTotal             || 0);
      t.maintenanceAndHousekeepingPaidByKbmTotal    += +(s.maintenanceAndHousekeepingPaidByKbmTotal    || 0);
      t.maintenanceAndHousekeepingPaidByOwnerTotal  += +(s.maintenanceAndHousekeepingPaidByOwnerTotal  || 0);
      t.mgntExpense                  += +(s.mgntExpense                  || 0);
      t.maintenancePaidByOwnerTotal  += +(s.maintenancePaidByOwnerTotal  || 0);
      t.housekeepingPaidByOwnerTotal += +(s.housekeepingPaidByOwnerTotal || 0);
      t.taxExpense                   += +(s.taxExpense                   || 0);
      t.totalExpense                 += +(s.totalExpense                 || 0);
      t.startingBalance              += +(r?.startingBalance             || 0);
      t.endingBalance                += +(s.endingBalance                || 0);
      t.discrepancyAmount            += +(r?.stmtReconcile?.discrepancyAmount || 0);
      t.maintenanceCreditTotal       += +(s.maintenanceCreditTotal       || 0);
      t.housekeepingCreditTotal      += +(s.housekeepingCreditTotal      || 0);
      if (r?.stmtReconcile?.expenseMatch === true)  t.matchCount    += 1;
      if (r?.stmtReconcile?.expenseMatch === false) t.mismatchCount += 1;
    }
    t.occupancyRateAvg = t.occupancyRateSum / t.rowCount;
    return t;
  }
  isInitialLoad = true;
  advanceSearchData: any = [];
  advanceSearchDataFilter: LazyLoadEvent = {};
  eventFilter: LazyLoadEvent = {};

  // Default date filters for previous month
  defaultStartDate: string;
  defaultEndDate: string;
  selectedMonthDate: Date; // For p-calendar month/year picker

  // Dashboard inputs (derived from selectedMonthDate)
  dashboardMonth: number;
  dashboardYear: number;

  //For reservation list to be shown on dialog
  formTitle: String;
  reservationForPredicate: any;
  showCreateReservationPopUp = false;

  // forms
  form1: UntypedFormGroup;
  formLoading = false;
  formAlert: String;
  mailComposeLoading = false;

  statuses: any;

  mailStatEntries: any = [
    { key: 'messageCount', icon: 'pi-envelope', label: 'Messages', toolTip: 'Messages Count' },
    { key: 'sentCount', icon: 'pi-send', label: 'Sent', toolTip: 'Sent Count' },
    { key: 'deliveredCount', icon: 'pi-check', label: 'Delivered', toolTip: 'Delivered Count' },
    { key: 'openedCount', icon: 'pi-eye', label: 'Opened', toolTip: 'Opened Count' },
    { key: 'deferredCount', icon: 'pi-clock', label: 'Deferred', toolTip: 'Deferred Count' },
    // { key: 'clickedCount', icon: 'pi-external-link', label: 'Clicked', toolTip: 'Clicked Count' },
    { key: 'bouncedCount', icon: 'pi-ban', label: 'Bounced', toolTip: 'Bounced Count' },
    // { key: 'droppedCount', icon: 'pi-minus-circle', label: 'Dropped', toolTip: 'Dropped Count' }
  ];

  validationMessages = {
    shortTermRentalId: [{ type: 'required', message: 'Required' }],
    emailAddress: [{ type: 'required', message: 'Required' },
      { type: 'email', message: 'Not a valid Email' }],
    referralSourceId: [{ type: 'required', message: 'Required' }],
    numAdults: [{ type: 'required', message: 'Required' }],
    numChildren: [{ type: 'required', message: 'Required' }],
    checkOutDate: [{ type: 'required', message: 'Required' }],
    checkInDate: [{ type: 'required', message: 'Required' }],
    firstName: [{ type: 'required', message: 'Required' }],
    lastName: [{ type: 'required', message: 'Required' }],
  };

  bulkUpdateValidationMessages = {
    statementMonth: [
      { type: 'required', message: 'Statement month is required' }
    ],
    properties: [],
    oldStatus: [
      { type: 'required', message: 'Old status is required' }
    ],
    newStatus: [
      { type: 'required', message: 'New status is required' },
      { type: 'sameStatus', message: 'Old and new status cannot be the same' }
    ]
  };
  referralSources: any[];
  properties: any[] = [];
  locations: any[] = [];
  numAdults: any[];
  searchName: any;
  searchData: { [k: string]: any; };
  activatedRoutePropertyCode: any;

  // Bulk update properties
  showBulkUpdateDialog = false;
  bulkUpdateForm: UntypedFormGroup;
  showRegenerateBulkDialog = false;
  regenerateBulkForm: UntypedFormGroup;
  regenerateBulkLoading = false;
  regenerateDialogType: 'bulk' | 'expenses' = 'bulk';

  // Bulk mail properties
  showBulkMailDialog = false;
  bulkMailMonthYear: Date = null;
  bulkMailPassword = '';
  bulkMailError = '';
  bulkMailLoading = false;
  bulkMailProgress = '';

  // Pre-send checklist — 7 items that must all be checked before the Send button is enabled.
  // State is persisted in localStorage keyed by month (e.g. stmt-checklist-2026-04) so it
  // survives browser refreshes but starts fresh for each new month.
  private readonly CHECKLIST_KEY_PREFIX = 'stmt-checklist-';
  bulkMailChecklist = {
    bankReconciled: false,
    qbPaymentsEntered: false,
    beginEndBalancesVerified: false,
    negativeBalancesReviewed: false,
    reservationsReconciled: false,
    statementsSpotChecked: false,
    accountingManagerApproval: false,
  };

  private get checklistStorageKey(): string {
    const d = this.bulkMailMonthYear || this.selectedMonthDate || new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${this.CHECKLIST_KEY_PREFIX}${y}-${m}`;
  }

  private loadChecklistFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.checklistStorageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        this.bulkMailChecklist = { ...this.bulkMailChecklist, ...saved };
      } else {
        this.resetChecklist(false);
      }
    } catch {
      this.resetChecklist(false);
    }
  }

  private saveChecklistToStorage(): void {
    try {
      localStorage.setItem(this.checklistStorageKey, JSON.stringify(this.bulkMailChecklist));
    } catch {
      // localStorage not available — checklist state is still functional in memory
    }
  }

  onChecklistChange(): void {
    this.saveChecklistToStorage();
  }

  resetChecklist(save = true): void {
    this.bulkMailChecklist = {
      bankReconciled: false,
      qbPaymentsEntered: false,
      beginEndBalancesVerified: false,
      negativeBalancesReviewed: false,
      reservationsReconciled: false,
      statementsSpotChecked: false,
      accountingManagerApproval: false,
    };
    if (save) {
      this.saveChecklistToStorage();
    }
  }

  get checklistCompletedCount(): number {
    return Object.values(this.bulkMailChecklist).filter(Boolean).length;
  }

  get checklistTotalCount(): number {
    return Object.keys(this.bulkMailChecklist).length;
  }
  statusList: any[] = [];
  statusListWithoutPublished: any[] = [];
  selectedStatementRow: any = null;
  stmtContextMenuItems: any[] = [];
  exportToExcelLoading = false;
  showDeleteConfirmDialog = false;
  deleteCodeValue = '';
  deleteStatementLoading = false;
  deleteCodeError = '';
  selectedLocationId: any;
  selectedLocationIds: number[] = [];
  selectedPropertyIds: any[] = [];
  private locationFilterDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly locationFilterDebounceMs = 400;
  private static readonly TOPIC_STATEMENT = 2600;

  // Checkbox selection for bulk operations
  selectedStatements: Set<number> = new Set();
  selectAllChecked = false;

  // Dashboard blocker count — emitted by the dashboard on each refresh
  criticalBlockers = 0;

  // Mark-paid dialog
  showMarkPaidDialog = false;
  markPaidRow: any = null;
  markPaidForm: UntypedFormGroup;
  markPaidLoading = false;
  markPaidError = '';
  readonly paymentMethodOptions = [
    { label: 'ACH / NACHA', value: 'ACH' },
    { label: 'Check', value: 'CHECK' },
    { label: 'Wire Transfer', value: 'WIRE' },
    { label: 'Manual Adjustment', value: 'MANUAL_ADJUSTMENT' }
  ];


  constructor(
    public authenticationService: AuthenticationService,
    public reservationService: ReservationService,
    public alertService: AlertService,
    public constantService: ConstantService,
    private messageService: MessageService,
    private formBuilder: UntypedFormBuilder,
    public router: Router,
    private _activatedRoute: ActivatedRoute,
    private propertyService: PropertyService,
    private location1: Location,
    private dialog: MatDialog,
    private ownerStmtService: OwnerStmtService,
    private ownerStatementMailService: OwnerStatementMailService,
    @Inject(MAT_DIALOG_DATA) data: any
  ) {
    this.statusList = this.ownerStmtService.stmtStatus;
    this.statusListWithoutPublished = this.ownerStmtService.stmtStatusWithoutPublished;
    this.formTitle = data.title,
      this.reservationForPredicate = data.reservation;
    this.referralSources = this.reservationService.referralSources;
    this.numAdults = this.reservationService.numAdults;
    if(this._activatedRoute.snapshot.paramMap.get('propertyCode'))
    {
      this.activatedRoutePropertyCode = this._activatedRoute.snapshot.paramMap.get('propertyCode');
    }
  }


  /** Receives the critical-blocker count from the dashboard on each refresh. */
  onBlockerCount(count: number) {
    this.criticalBlockers = count;
  }

  // ---- Mark Paid dialog ----
  openMarkPaidDialog(row: any) {
    if (!row) return;
    this.markPaidRow = row;
    const now = new Date();
    this.markPaidForm = this.formBuilder.group({
      paymentMethod: ['CHECK', Validators.required],
      paidDate: [new Date(now.getFullYear(), now.getMonth(), now.getDate()), Validators.required],
      paymentReference: ['']
    });
    this.markPaidError = '';
    this.showMarkPaidDialog = true;
  }

  cancelMarkPaid() {
    this.showMarkPaidDialog = false;
    this.markPaidRow = null;
    this.markPaidError = '';
  }

  confirmMarkPaid() {
    if (!this.markPaidForm?.valid) {
      this.markPaidForm?.markAllAsTouched();
      return;
    }
    const fv = this.markPaidForm.value;
    const ownerStmtId = this.markPaidRow?.ownerStmtId;
    if (!ownerStmtId) return;
    this.markPaidLoading = true;
    this.markPaidError = '';
    this.ownerStmtService.markManualPaid(ownerStmtId, {
      paymentStatus: 'PAID',
      paymentMethod: fv.paymentMethod,
      paidDate: moment(fv.paidDate).format('YYYY-MM-DD'),
      paymentReference: fv.paymentReference || null
    }).pipe(first()).subscribe({
      next: () => {
        this.markPaidLoading = false;
        this.showMarkPaidDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Marked Paid',
          detail: `${this.markPaidRow?.propertyCode || 'Statement'} marked as PAID.`,
          life: 3000
        });
        this.loadLazy(this.lazyLoadEvent);
      },
      error: (err) => {
        // Do NOT reset markPaidLoading on error — a 504 means the mark-paid call may have already
        // completed on the server. Keep button disabled to block re-submit.
        this.markPaidError = 'Mark paid may have completed. Please refresh before retrying.';
        console.log('confirmMarkPaid FAIL: ' + JSON.stringify(err?.error?.message || err?.message));
      }
    });
  }

  // First-visit onboarding banner — shown until the user clicks Dismiss.
  // Persists in localStorage so it never shows again per browser.
  showOnboardingBanner = false;
  private readonly ONBOARDING_KEY = 'kbm_owner_stmts_onboarded_v1';

  dismissOnboarding(): void {
    localStorage.setItem(this.ONBOARDING_KEY, '1');
    this.showOnboardingBanner = false;
  }

  ngOnInit() {
    this.showOnboardingBanner = !localStorage.getItem(this.ONBOARDING_KEY);
    this.initializeDefaultDates();
    this.fetchProperties();
    this.initializeLocations();
    this._activatedRoute.queryParams.subscribe(params => {
      this.searchData = params['searchName'];
    });
    if(this.searchData != null && this.searchData != undefined){
      this.searchName = this.searchData;
    }
    this.formBuild();
    this.stmtContextMenuItems = [
      { label: 'View Statement', icon: 'pi pi-eye', command: () => this.viewStatement() },
      { label: 'Edit Statement', icon: 'pi pi-pencil', command: () => this.editStatement() },
      { label: 'Mail Statement', icon: 'pi pi-envelope', command: () => this.mailStatement() },
      { label: 'Mark Paid', icon: 'pi pi-check-circle', command: () => this.openMarkPaidDialog(this.selectedStatementRow) },
      { label: 'View Sentbox', icon: 'pi pi-envelope', command: () => this.viewSentbox() },
      { label: 'View Statement Rez', icon: 'pi pi-list', command: () => this.viewStatementReservations() },
      { label: 'View Expenses', icon: 'pi pi-wallet', command: () => this.viewExpenses() },
      { label: 'Delete', icon: 'pi pi-trash', command: () => this.openDeleteStatementConfirm(), styleClass: 'stmt-context-menu-delete' },
    ];
    // this.fetchProperties();
  }

  openStmtCm(event: Event, cm: any, row: any) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedStatementRow = row;
    cm.show(event);
  }

  viewStatement() {
    if (!this.selectedStatementRow) return;
    const ownerStmtId = this.selectedStatementRow.ownerStmtId;
    this.location1.go('/owner-stmt-preview/' + ownerStmtId);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = '1500px';
    dialogConfig.width = '100vw';
    dialogConfig.maxHeight = '100vh';
    dialogConfig.height = '95vh';
    dialogConfig.panelClass = 'stmt-view-dialog';
    dialogConfig.data = { ownerStmt: this.selectedStatementRow, title: 'View Statement', isDialogMode: true };
    const dialogRef = this.dialog.open(StatementsComponent, dialogConfig);
    dialogRef.componentInstance.doUpdate?.subscribe(() => dialogRef.close());
    dialogRef.componentInstance.doDelete?.subscribe(() => dialogRef.close());
    dialogRef.afterClosed().subscribe(() => {
      this.location1.go('/owner-statements');
    });
  }

  editStatement() {
    if (!this.selectedStatementRow) return;
    this.onOpenFormEdit(this.selectedStatementRow);
  }

  mailStatement() {
    if (!this.selectedStatementRow) return;
    this.ownerStatementMailService.openStatementMailCompose(this.selectedStatementRow, {
      onLoading: (v) => this.mailComposeLoading = v,
      onPublishRequired: (summary, detail) => this.messageService.add({ severity: 'error', summary, detail, life: 5000 })
    }).subscribe((sent) => {
      if (sent) {
        this.loadLazy(this.lazyLoadEvent);
      }
    });
  }

 
  viewStatementReservations() {
    if (!this.selectedStatementRow) return;
    const row = this.selectedStatementRow;
    const startDate = row?.startDate ? moment(row.startDate).format('YYYY-MM-DD') : null;
    const endDate = row?.endDate
      ? moment(row.endDate).format('YYYY-MM-DD')
      : (row?.startDate ? moment(row.startDate).endOf('month').format('YYYY-MM-DD') : null);
    const queryParams: any = {};
    if (row?.shortTermRentalId) {
      queryParams.shortTermRentalId = row.shortTermRentalId;
    }
    if (startDate) {
      queryParams.startDate = startDate;
    }
    if (endDate) {
      queryParams.endDate = endDate;
    }
    if (row?.propertyCode) {
      queryParams.propertyCode = row.propertyCode;
    }
    const queryString = Object.keys(queryParams)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&');
    const url = `/owner-stmt-rez-list${queryString ? '?' + queryString : ''}`;
    window.open(url, '_blank');
  }

  ngAfterViewInit() {
    // Set the default filter values in the table after view is initialized
    if (this.table && this.isInitialLoad) {
      setTimeout(() => {
        this.table.filter(this.defaultStartDate, 'startDate', 'in');
        this.table.filter(this.defaultEndDate, 'endDate', 'in');
        const initialEvent: LazyLoadEvent = {
          first: 0,
          rows: 300,
          filters: {
            'startDate': { value: this.defaultStartDate, matchMode: 'in' },
            'endDate': { value: this.defaultEndDate, matchMode: 'in' }
          },
          multiSortMeta: [
            { field: 'startDate', order: -1 },
            { field: 'propertyCode', order: 1 }
          ]
        };
        this.loadLazy(initialEvent);
      }, 50);
    }
  }


  updateOwnerStmtStatus(ownerStmt: any, status: any) {
    try {
      this.formLoading = true;
      this.ownerStmtService.updateOwnerStmtStatus(ownerStmt?.ownerStmtId, status)
        .subscribe(
          (res: any) => {
            this.formLoading = false;
            // this.loadLazy(this.lazyLoadEvent);
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: `${ownerStmt?.propertyCode} statement status updated successfully`, life: 3000 });
          },
          (err: any) => {
            // Do NOT reset formLoading on error — a 504 means the status update may have already
            // completed on the server. Keep button disabled to block re-submit.
            console.log('updateOwnerStmtStatus FAIL: ' + JSON.stringify(err));
            this.messageService.add({ severity: 'error', summary: 'Update Failed', detail: 'Status update may have completed. Please refresh before retrying.', life: 5000 });
          },
          () => { });
    } catch (error) {
      this.formLoading = false;
      console.log('Form Invalid: ' + JSON.stringify(error));
      this.alertService.error(error);
    }
  }

  /**
   * Initialize default dates to previous month's start and end dates
   */
  private initializeDefaultDates() {
    const previousMonth = moment().subtract(1, 'months');
    this.defaultStartDate = previousMonth.clone().startOf('month').format('YYYY-MM-DD');
    this.defaultEndDate = previousMonth.clone().endOf('month').format('YYYY-MM-DD');
    this.selectedMonthDate = previousMonth.toDate();
    this.dashboardMonth = previousMonth.month() + 1;
    this.dashboardYear = previousMonth.year();
  }

  /** Dashboard event handlers */
  onDashboardStatusFilter(statuses: number[]) {
    if (this.table) {
      this.table.filter(statuses, 'stmtStatus', 'in');
    }
  }

  onDashboardShowUnemailed() {
    // Filter to PUBLISHED + sentCount=0; concrete column wiring depends on the
    // existing filter setup. For now, filter to PUBLISHED status as a starting
    // point; user can sort by sentDate column.
    this.onDashboardStatusFilter([7]);
  }

  /**
   * Handle month/year selection from p-calendar - sets startDate and endDate filters
   * based on selected month's first and last day
   */
  onMonthYearSelect(selectedDate: Date) {
    if (!selectedDate) {
      // Clear the date filters if month is cleared
      this.defaultStartDate = '';
      this.defaultEndDate = '';
      this.selectedMonthDate = null;
      if (this.table) {
        this.table.filter('', 'startDate', 'in');
        this.table.filter('', 'endDate', 'in');
        if (this.lazyLoadEvent) {
          if (this.lazyLoadEvent?.filters) {
            delete this.lazyLoadEvent?.filters['startDate'];
            delete this.lazyLoadEvent?.filters['endDate'];
          }
          this.loadLazy(this.lazyLoadEvent);
        }
      }
      return;
    }

    const selectedMoment = moment(selectedDate);
    this.defaultStartDate = selectedMoment.clone().startOf('month').format('YYYY-MM-DD');
    this.defaultEndDate = selectedMoment.clone().endOf('month').format('YYYY-MM-DD');
    this.dashboardMonth = selectedMoment.month() + 1;
    this.dashboardYear = selectedMoment.year();

    if (this.table) {
      this.table.filter(this.defaultStartDate, 'startDate', 'in');
      this.table.filter(this.defaultEndDate, 'endDate', 'in');
      if (this.lazyLoadEvent) {
        if (!this.lazyLoadEvent.filters) {
          this.lazyLoadEvent.filters = {};
        }
        this.lazyLoadEvent.filters['startDate'] = { value: this.defaultStartDate, matchMode: 'in' };
        this.lazyLoadEvent.filters['endDate'] = { value: this.defaultEndDate, matchMode: 'in' };
        this.loadLazy(this.lazyLoadEvent);
      } else {
        const event: LazyLoadEvent = {
          first: 0,
          rows: 300,
          filters: {
            'startDate': { value: this.defaultStartDate, matchMode: 'in' },
            'endDate': { value: this.defaultEndDate, matchMode: 'in' }
          }
        };
        this.loadLazy(event);
      }
    }
  }


  onMultiselectFilterChange(selectedValues: any[], filterField: string) {
    // Do NOT call this.table.filter() here — it fires onLazyLoad before
    // lazyLoadEvent.filters is updated, causing the server call to go out
    // without the new filter value and overwriting the filter we set below.
    // All filtering is server-side via loadLazy; the table.filter() path
    // is for client-side filtering only and is unnecessary here.
    if (this.lazyLoadEvent) {
      if (!this.lazyLoadEvent.filters) {
        this.lazyLoadEvent.filters = {};
      }
      if (selectedValues && selectedValues.length > 0) {
        this.lazyLoadEvent.filters[filterField] = { value: selectedValues, matchMode: 'in' };
      } else {
        delete this.lazyLoadEvent.filters[filterField];
      }
      this.lazyLoadEvent.first = 0;
      this.loadLazy(this.lazyLoadEvent);
    } else {
      const event: LazyLoadEvent = {
        first: 0,
        rows: 300,
        filters: {
          [filterField]: { value: selectedValues, matchMode: 'in' }
        }
      };
      this.loadLazy(event);
    }
  }

  onPropertyFilterChange(selectedValues: any[]) {
    this.onMultiselectFilterChange(selectedValues, 'shortTermRentalId');
  }

  initializeLocations() {
    this.locations = [
      { name: 'All Locations', locationId: null, value: null },
      ...this.constantService.locations
    ];
  }

  ngOnDestroy(): void {
    if (this.locationFilterDebounceTimer) {
      clearTimeout(this.locationFilterDebounceTimer);
      this.locationFilterDebounceTimer = null;
    }
  }

  onLocationFilterChange(selectedLocationIds: number[]) {
    this.selectedLocationIds = Array.isArray(selectedLocationIds) ? selectedLocationIds : [];
    this.selectedLocationId = this.selectedLocationIds.length === 1 ? this.selectedLocationIds[0] : null;

    if (this.locationFilterDebounceTimer) {
      clearTimeout(this.locationFilterDebounceTimer);
    }
    this.locationFilterDebounceTimer = setTimeout(() => {
      this.applyLocationFilterChange();
      this.locationFilterDebounceTimer = null;
    }, this.locationFilterDebounceMs);
  }

  private applyLocationFilterChange() {
    this.locationMultiSelect?.hide();
    const hasSelection = this.selectedLocationIds.length > 0;
    if (!hasSelection) {
      if (this.lazyLoadEvent) {
        if (!this.lazyLoadEvent.filters) {
          this.lazyLoadEvent.filters = {};
        }
        delete this.lazyLoadEvent.filters['locationId'];
        this.lazyLoadEvent.first = 0;
        this.loadLazy(this.lazyLoadEvent);
      } else {
        const event: LazyLoadEvent = {
          first: 0,
          rows: 300,
          filters: {}
        };
        this.loadLazy(event);
      }
    } else {
      if (this.lazyLoadEvent) {
        if (!this.lazyLoadEvent.filters) {
          this.lazyLoadEvent.filters = {};
        }
        this.lazyLoadEvent.filters['locationId'] = {
          value: this.selectedLocationIds,
          matchMode: 'in'
        };
        this.lazyLoadEvent.first = 0;
        this.loadLazy(this.lazyLoadEvent);
      } else {
        const event: LazyLoadEvent = {
          first: 0,
          rows: 300,
          filters: {
            'locationId': { value: this.selectedLocationIds, matchMode: 'in' }
          }
        };
        this.loadLazy(event);
      }
    }
  }

  onStatusFilterChange(selectedValues: any[]) {
    this.onMultiselectFilterChange(selectedValues, 'stmtStatus');
  }

 

  fetchProperties() {
    try {
      this.propertyService.propertySummaryIndividualPropertyList()
        .subscribe(
          res => {
            // console.log('findAllPropertiesSummary: ' + JSON.stringify(res));
            this.properties = res.data.properties;
          },
          err => {
            console.log('Feature-Update FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { });
    } catch (error) {
      console.log('Form Invalid: ' + JSON.stringify(error));
      this.alertService.error(error);
    }
  }

  formBuild() {
    this.form1 = this.formBuilder.group({
      shortTermRentalId: ['', Validators.required],
      referralSourceId: ['', Validators.required],
      numAdults: ['', Validators.required],
      numChildren: ['', Validators.required],
      checkOutDate: ['', Validators.required],
      checkInDate: ['', Validators.required],
      guestProfile: this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        emailAddress: ['', Validators.compose([Validators.required, Validators.email])],
        guestCompanions: new UntypedFormArray([]),
      }),
    });
  }




  loadLazy(event: LazyLoadEvent) {
    try {
      // Ensure filters object exists to avoid undefined access
      event.filters = event.filters || {};
      console.log(this.advanceSearchDataFilter)
      if(event.filters && Object.keys(event.filters).length > 0)
      {
        this.eventFilter = event.filters;
      }
      if(this.advanceSearchDataFilter.filters && Object.keys(this.advanceSearchDataFilter.filters).length > 0)
      {
        //combine objects
        event.filters = { ...this.advanceSearchDataFilter.filters, ...event.filters };
      }
      this.loading = true;
      this.lazyLoadEvent = event;
      console.log('EVENT ' + JSON.stringify(event));
      console.log(
 
        ' event.first:' + event.first
        + '  |  event.rows:' + event.rows
        + '  |  event.sortField:' + event.sortField
        + '  |  event.sortOrder:' + event.sortOrder
        + '  |  event.multiSortMeta:' + JSON.stringify(event.multiSortMeta)
        + '  |  event.globalFilter:' + event.globalFilter
        + '  |  event.filters:' + JSON.stringify(event.filters)
      );
      
     const hasUserSort = event.multiSortMeta && event.multiSortMeta.length > 0;
     const isInitialPageLoad = event.first === 0 && this.isInitialLoad;
      
      if (isInitialPageLoad && !hasUserSort) {
        event.multiSortMeta = [];
        event.multiSortMeta.push({ field: 'startDate', order: -1 }, { field: 'propertyCode', order: 1 });
        
        // Apply default date filters for previous month on initial load
        if (this.defaultStartDate && !event.filters['startDate']) {
          event.filters['startDate'] = { value: this.defaultStartDate, matchMode: 'in' };
        }
        if (this.defaultEndDate && !event.filters['endDate']) {
          event.filters['endDate'] = { value: this.defaultEndDate, matchMode: 'in' };
        }
        
        this.isInitialLoad = false; // Mark initial load as complete
      } else if (hasUserSort) {
        this.isInitialLoad = false;
      }
      
      if (event.multiSortMeta && event.multiSortMeta.length > 0) {
        this.multiSortMeta = event.multiSortMeta;
      }




     if (this.reservationForPredicate != null && this.reservationForPredicate.shortTermRentalId != null) {
        let reservationFilter = { 'propertyCode': { 'value': this.reservationForPredicate.propertyCode, 'matchMode': 'in' } };
        Object.assign(event.filters, reservationFilter);
        if (!hasUserSort) {
          event.multiSortMeta = [];
          event.multiSortMeta.push({ field: 'checkInDateTime', order: -1 });
          this.multiSortMeta = event.multiSortMeta;
        }
      }

      if (event.filters?.checkInDateTime) {
        event.filters.checkInDateTime.value = moment(event.filters.checkInDateTime.value).format('MM/DD/YYYY');
      }

      if (event.filters?.checkOutDateTime) {
        event.filters.checkOutDateTime.value = moment(event.filters.checkOutDateTime.value).format('MM/DD/YYYY');
      }


      if(this.activatedRoutePropertyCode)
      {
        // show only activities that are assigned to this activityFeeId
        let rateBucketIdFilter = { 'propertyCode': { 'value': this.activatedRoutePropertyCode, 'matchMode': 'in' } };
        Object.assign(event.filters, rateBucketIdFilter);
      }
      if (event.filters?.global) {
        event.filters.global.value = event.filters.global.value.trim();
        this.searchName = event.filters.global.value.trim();
      }
      if (event.globalFilter == null && this.searchName != null && this.searchName != "") {
        event.filters.global = {
          matchMode:"contains",
          value:this.searchName
        }
        event.globalFilter = this.searchName;
      }
      const pageNumber = Math.round(event.first / event.rows);
      this.ownerStmtService.findStmtListFilter(
        pageNumber, event.rows,
        event.sortField,
        event.sortOrder,
        event.multiSortMeta,
        event.globalFilter,
        event.filters).pipe(first()).subscribe(

        res => {
          // console.log('reservations are: ' + JSON.stringify(apiResponse));
          this.datasource = res.data.ownerStmts;
          this.recordTotal = res.page.recordTotal;
          this.reservations = res.data.ownerStmts;
         // this.reservations = this.datasource.slice(event.first, (event.first + event.rows));
          this.location1.go('/owner-statements');
        },
        error => {
          console.log('USER LIST!: ' + JSON.stringify(error));
          this.alertService.error(error);
          this.loading = false;
        },
        () => { this.loading = false; }
      );

      if (this.datasource) {
        this.reservations = this.datasource.slice(event.first, (event.first + event.rows));
      }
    } catch (error) {
      console.log('loadLazy(' + event + ')' + error);
      this.loading = false;
    }
  }
  onRowSelect(event) {
    try {
      // use this for adding query params
      // this.router.navigate(['reservation-edit'], { queryParams: { reservationId: event.data.reservationId } });
      // this.router.navigate(['reservations', event.data.reservationId]);
      this.onOpenFormEdit(event.data);
    } catch (error) {
      console.log('onRowSelect(' + event + ')' + error);
    }
  }

  getResStatusName(resStatus) {
   // return 'Open';
    if (resStatus == null || resStatus === '') {
      return '';
    }
    return this.ownerStmtService.stmtStatus.find((item) => item.value == resStatus)?.label ?? '';
  }

  getResStatusColor(resStatus) {
    return 'lightgreen';
    // return this.constantService.reservationStatuses.find(function(item) { return item.value === resStatus }).color;
  }
  clear(table: Table) {
    table.clear();
  }

  reloadPage() {
    window.location.reload();
  }

  /*********
   * Add New Button
   *
   */
  onOpenFormAdd() {

    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;

    dialogConfig.maxWidth = "100vw";
    dialogConfig.width = "95vw";
    dialogConfig.maxHeight = "50vw";
    dialogConfig.height = "95vh";

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      title: 'Add Reservation'
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(ReservationContainerComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
    // dialogRef.componentInstance.doAdd.subscribe(
    // 	data => {
    // 		console.log('Dialog output:', data);

    // 		// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
    // 		this.addReservationCB(data);

    // 		// close the dialogBox
    // 		dialogRef.close();
    // 	});
  }


  /*********
   * Edit Button
   *
   */
  // onOpenFormEdit(reservation: any) {

  // 	const dialogConfig = new MatDialogConfig();

  // 	// settings
  // 	dialogConfig.disableClose = false;
  // 	dialogConfig.autoFocus = true;
  // 	dialogConfig.hasBackdrop = true;
  //   dialogConfig.maxWidth = "100vw";
  // 		dialogConfig.width = "95vw";
  //     dialogConfig.maxHeight = "50vw";
  // 		dialogConfig.height = "95vh";

  // 	// pass data, ie rateBuicket: this.shortTermRentalId,
  // 	dialogConfig.data = {
  // 		reservationId: reservation.reservationId,
  //     reservation: reservation,
  //     showCloseButton: true,
  // 		title: 'Edit Order'
  // 	};

  // 	// open the popup and get a reference to it called dialogRef
  // 	const dialogRef = this.dialog.open(ReservationContainerComponent, dialogConfig);
  //   this.location1.go('/reservations-edit/' + reservation.reservationId);

  // 	// to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
  // 	// dialogRef.componentInstance.doUpdate.subscribe(
  // 	// 	data => {
  // 	// 		console.log('Dialog output:', data);

  // 	// 		// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
  // 	// 		this.updateReservationCB(data);

  // 	// 		// close the dialogBox
  // 	// 		dialogRef.close();
  // 	// 	});

  // 	// dialogRef.componentInstance.doDelete.subscribe(
  // 	// 	data => {
  // 	// 		console.log('Dialog output:', data);

  // 	// 		// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
  // 	// 		this.deleteReservationCB(data);

  // 	// 		// close the dialogBox
  // 	// 		dialogRef.close();
  // 	// 	});
  // }

  /*********
   * Edit Button
   *
   */
  onOpenFormEdit(reservation: any) {

    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "100vw";
    dialogConfig.width = "95vw";
    dialogConfig.maxHeight = "50vw";
    dialogConfig.height = "95vh";
    dialogConfig.panelClass = "edit-order-mobile";

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      reservationId: reservation.ownerStmtId,
      reservation: reservation,
      showCloseButton: true,
      title: 'Edit Statement',
      shortTermRentalId: reservation.shortTermRentalId,
      publishBlocked: this.criticalBlockers > 0,
      criticalBlockers: this.criticalBlockers
    };
    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(StatementComponent, dialogConfig);
    // this.location1.go('/reservations-edit/' + reservation.reservationId);
    this.location1.go('/owner-statement/' + reservation.ownerStmtId);

    dialogRef.afterClosed().subscribe(() => {
      if (this.table) {
        this.table.selection = null;
      }
      this.reservation = null;
    });

    // to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
    dialogRef.componentInstance.doUpdate.subscribe(
      data => {
        console.log('Dialog output:', data);
        this.searchName = data;

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        // this.updateReservationCB(data);
        this.loadLazy(this.lazyLoadEvent);

        // close the dialogBox
        dialogRef.close();
      });

    // dialogRef.componentInstance.doDelete.subscribe(
    // 	data => {
    // 		console.log('Dialog output:', data);

    // 		// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
    // 		this.deleteReservationCB(data);

    // 		// close the dialogBox
    // 		dialogRef.close();
    // 	});
  }

  /**
   *
   * Dialog from Form Callbacks
   * To edit the list show add/edit/delete in real time from the popup form
   *
   */
  addReservationCB(reservation: any) {
    console.log('Adding Reservation: ' + JSON.stringify(reservation));
    // if we don't have a tests array yet...
    if (!this.reservations) {
      this.reservations = [];
    }
    // unshift adds to the front of the array (), push adds to the back
    this.reservations.unshift(reservation);

    // no idea what this is but it is required to update the array
    this.reservations = [...this.reservations];

    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reservation ' + reservation.reservationId + ' Added', life: 3000 });
  }

  updateReservationCB(reservation: any) {
    console.log('Updating Reservation: ' + JSON.stringify(reservation));
    let itemIndex = this.reservations.findIndex((item: any) => item.reservationId === reservation.reservationId);
    this.reservations[itemIndex] = reservation;

    // no idea what this is but it is required to update the array
    this.reservations = [...this.reservations];

    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reservation ' + reservation.reservationId + ' Updated', life: 3000 });
  }

  deleteReservationCB(reservation: any) {
    console.log('Removing Reservation: ' + reservation);
    this.reservations.splice(this.reservations.indexOf(reservation), 1);
    this.recordTotal = this.reservations.length;

    // no idea what this is but it is required to update the array
    this.reservations = [...this.reservations];

    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reservation ' + reservation.reservationId + ' Deleted', life: 3000 });
  }

  onAdvanceSearch() {
    try {

      const dialogConfig = new MatDialogConfig();

      // settings
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.hasBackdrop = true;
      dialogConfig.maxWidth = "45vw";
      dialogConfig.width = "40vw";
      // dialogConfig.height = "45vw";
      // dialogConfig.maxHeight = "60vw";

      // pass data, ie rateBuicket: this.shortTermRentalId,
      dialogConfig.data = {
        advanceSearchData: this.advanceSearchData,
        title: 'Advance Search'
      };

      // open the popup and get a reference to it called dialogRef
      const dialogRef = this.dialog.open(ReservationAdvanceSearchComponent, dialogConfig);
      dialogRef.componentInstance.doSearchCB.subscribe(
        data => {
          this.advanceSearchData = data;
          let event = {"first":0,"rows":50,"sortOrder":1, "globalFilter":null,"multiSortMeta":[]};
          if(this.advanceSearchData)
          {
            this.advanceSearchDataFilter.filters = {};
            if(this.advanceSearchData.locationIds)
            {
              let locationIds = [];
              this.advanceSearchData.locationIds.forEach(element => {
                const id = element != null && typeof element === 'object' ? element.locationId : element;
                locationIds.push(id);
              });
              this.advanceSearchDataFilter.filters["locationId"] = {"value":locationIds,"matchMode":"in"}
            }
            if(this.advanceSearchData.shortTermRentalIds)
            {
              let shortTermRentalIds = [];
              this.advanceSearchData.shortTermRentalIds.forEach(element => {
                const id = element != null && typeof element === 'object' ? element.shortTermRentalId : element;
                shortTermRentalIds.push(id);
              });
              this.advanceSearchDataFilter.filters["shortTermRentalVO.shortTermRentalId"] = {"value":shortTermRentalIds,"matchMode":"in"}
            }
            if(this.advanceSearchData.arrivalStartDate)
            {
              this.advanceSearchDataFilter.filters["checkInDateStart"] = {"value":moment(this.advanceSearchData.arrivalStartDate).format('MM/DD/YYYY'),"matchMode":"in"}
            }
            if(this.advanceSearchData.arrivalEndDate)
            {
              this.advanceSearchDataFilter.filters["checkInDateEnd"] = {"value":moment(this.advanceSearchData.arrivalEndDate).format('MM/DD/YYYY'),"matchMode":"in"}
            }
            if(this.advanceSearchData.saleStartDate)
            {
              this.advanceSearchDataFilter.filters["saleDateStart"] = {"value":moment(this.advanceSearchData.saleStartDate).format('MM/DD/YYYY'),"matchMode":"in"}
            }
            if(this.advanceSearchData.saleEndDate)
            {
              this.advanceSearchDataFilter.filters["saleDateEnd"] = {"value":moment(this.advanceSearchData.saleEndDate).format('MM/DD/YYYY'),"matchMode":"in"}
            }
				if(this.advanceSearchData.checkoutStartDate)
				{
				  this.advanceSearchDataFilter.filters["checkOutDateStart"] = {"value":moment(this.advanceSearchData.checkoutStartDate).format('MM/DD/YYYY'),"matchMode":"in"}
				}
				if(this.advanceSearchData.checkoutEndDate)
				{
				  this.advanceSearchDataFilter.filters["checkOutDateEnd"] = {"value":moment(this.advanceSearchData.checkoutEndDate).format('MM/DD/YYYY'),"matchMode":"in"}
				}
				if(this.advanceSearchData.occupyingStartDate)
				{
				  this.advanceSearchDataFilter.filters["stayDateStart"] = {"value":moment(this.advanceSearchData.occupyingStartDate).format('MM/DD/YYYY'),"matchMode":"in"}
				}
				if(this.advanceSearchData.occupyingEndDate)
				{
				  this.advanceSearchDataFilter.filters["stayDateEnd"] = {"value":moment(this.advanceSearchData.occupyingEndDate).format('MM/DD/YYYY'),"matchMode":"in"}
				}
            if(this.advanceSearchData.stmtStatus)
            {
              this.advanceSearchDataFilter.filters["stmtStatus"] = { "value": this.advanceSearchData.stmtStatus,"matchMode":"in"}
            }
            if(this.advanceSearchData.reservationType)
            {
              this.advanceSearchDataFilter.filters["reservationType"] = {"value": this.advanceSearchData.reservationType,"matchMode":"in"}
            }
            if(this.advanceSearchData.lastName)
            {
              this.advanceSearchDataFilter.filters["guestProfileVO.lastName"] = {"value": this.advanceSearchData.lastName,"matchMode":"in"}
            }
            if(this.advanceSearchData.wildcardSearch)
            {
              this.advanceSearchDataFilter.filters["wildcardSearch"] = {"value": this.advanceSearchData.wildcardSearch,"matchMode":"in"}
            }
          }
          if(this.eventFilter && Object.keys(this.eventFilter).length > 0)
          {
            //combine objects
            this.eventFilter = { ...this.eventFilter, ...this.advanceSearchDataFilter.filters};
            event['filters'] = this.eventFilter;
          }
          this.loadLazy(event);
          // close the dialogBox
          dialogRef.close();
        });
    } catch (error) {
      console.log('onRowSelect(' + this.advanceSearchDataFilter + ')' + error);
    }
  }

  openGenerateStatementByDatePopUp() {
    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "40vw";
    dialogConfig.width = "20vw";
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = "create-reservation-popup";
    // dialogConfig.maxHeight = "50vw";
    // dialogConfig.height = "95vh";

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      showCloseButton: true,
      openNewTab: true,
      showGenerateFormByDate: true,
      showGenerateForm: false,
      showBulkGenerateForm: false
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(StatementCreateComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
    dialogRef.componentInstance.doUpdate.subscribe(
      data => {
        console.log('Dialog output:', data);

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        this.loadLazy(this.lazyLoadEvent);

        // close the dialogBox
        dialogRef.close();
      });
  }

  openGenerateStatementPopUp() {
    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "40vw";
    dialogConfig.width = "20vw";
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = "create-reservation-popup";
    // dialogConfig.maxHeight = "50vw";
    // dialogConfig.height = "95vh";

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      showCloseButton: true,
      openNewTab: true,
      showGenerateFormByDate: false,
      showGenerateForm: true,
      showBulkGenerateForm: false
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(StatementCreateComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
    dialogRef.componentInstance.doUpdate.subscribe(
      data => {
        console.log('Dialog output:', data);

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        this.loadLazy(this.lazyLoadEvent);

        // close the dialogBox
        dialogRef.close();
      });
  }

  openGenerateBulkStatementPopUp() {
    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.maxWidth = "40vw";
    dialogConfig.width = "20vw";
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = "create-reservation-popup";
    // dialogConfig.maxHeight = "50vw";
    // dialogConfig.height = "95vh";

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      showCloseButton: true,
      openNewTab: true,
      showGenerateFormByDate: false,
      showGenerateForm: false,
      showBulkGenerateForm: true
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(StatementCreateComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
    dialogRef.componentInstance.doCloseDialog.subscribe(
      data => {
        console.log('Dialog output:', data);


        dialogRef.close();
        this.messageService.add({ severity: 'Process Started', summary: 'In Background Generate Statement Process Started Successfully', detail: "It will take around 10 minute for generate all statement" });

      });
  }

  openRegeneratePopUp(type: 'bulk' | 'expenses') {
    const lastMonth = moment().subtract(1, 'months');
    this.regenerateBulkForm = this.formBuilder.group({
      monthYear: [lastMonth.toDate(), Validators.required]
    });
    this.regenerateDialogType = type;
    this.showRegenerateBulkDialog = true;
  }

  confirmRegenerateBulk() {
    if (!this.regenerateBulkForm?.valid) {
      this.regenerateBulkForm?.markAllAsTouched();
      return;
    }
    const selectedDate = moment(this.regenerateBulkForm.get('monthYear')?.value);
    const month = selectedDate.month() + 1; // 1-based for API
    const year = selectedDate.year();
    this.regenerateBulkLoading = true;

    const apiCall = this.regenerateDialogType === 'expenses'
      ? this.ownerStmtService.regenerateStmtExpenses(month, year)
      : this.ownerStmtService.regenerateBulkStatement(month, year);

    const successMessage = this.regenerateDialogType === 'expenses'
      ? 'Regenerate statement expenses completed successfully.'
      : 'Regenerate bulk statement completed successfully.';

    const errorMessage = this.regenerateDialogType === 'expenses'
      ? 'Regenerate statement expenses failed. Please try again.'
      : 'Regenerate bulk statement failed. Please try again.';

    apiCall.pipe(first()).subscribe(
      (res: any) => {
        this.regenerateBulkLoading = false;
        this.showRegenerateBulkDialog = false;
        this.regenerateBulkForm?.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res?.message || successMessage,
          life: 3000
        });
        this.loadLazy(this.lazyLoadEvent);
      },
      (err: any) => {
        // Do NOT reset regenerateBulkLoading on error — a 504 means the regeneration may have
        // already completed on the server. Keep button disabled to block re-submit.
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Regeneration may have completed. Please close this dialog and refresh before retrying.',
          life: 7000
        });
        console.log('confirmRegenerateBulk FAIL: ' + JSON.stringify(err?.error?.message || err?.message));
      }
    );
  }

  openHelpAlert() {
    this.alertService.success("If you need help or are experiencing any difficulties, please call Dipesh on Ring Central at extension 907.", true);
  }

  exportSelected(exporttype: any) {
    this.importExportComponent.exporData(exporttype, this.reservations, 'Reservations Info');
  }


  exportToExcel(): void {
    if (this.exportToExcelLoading) {
      return;
    }
    this.exportToExcelLoading = true;
    setTimeout(() => {
      this.runExportToExcel();
    }, 0);
  }


  getLocationName(locationId) {
    let location = this.locations.filter((loc) => loc.locationId == locationId)?.[0];
    return location?.name || locationId;
  }

  private runExportToExcel(): void {
    const total = this.recordTotal || 0;
    const rowsToFetch = total > 0 ? Math.min(total, 50000) : 10000;
    const event: LazyLoadEvent = {
      first: 0,
      rows: rowsToFetch,
      filters: this.lazyLoadEvent?.filters ? { ...this.lazyLoadEvent.filters } : {},
      multiSortMeta: this.lazyLoadEvent?.multiSortMeta ? [...this.lazyLoadEvent.multiSortMeta] : [],
      sortField: this.lazyLoadEvent?.sortField,
      sortOrder: this.lazyLoadEvent?.sortOrder,
      globalFilter: this.lazyLoadEvent?.globalFilter
    };
    const pageNumber = 0;
    this.ownerStmtService.findStmtListFilter(
      pageNumber,
      event.rows,
      event.sortField,
      event.sortOrder,
      event.multiSortMeta,
      event.globalFilter,
      event.filters
    ).pipe(first()).subscribe({
      next: (res) => {
        const list = res?.data?.ownerStmts || [];
        this.buildAndDownloadExcel(list);
        this.messageService.add({
          severity: 'success',
          summary: 'Export Complete',
          detail: `Exported ${list.length} statement(s) to Excel`,
          life: 3000
        });
      },
      error: (err) => {
        console.error('Export failed:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: err?.message || 'Failed to export statements',
          life: 5000
        });
      },
      complete: () => {
        this.exportToExcelLoading = false;
      }
    });
  }

  private getStmtStatusLabelForExport(r: any): string {
    if (!r) {
      return '';
    }
    const s = r.stmtStatus;
    if (s != null && typeof s === 'object' && s.label != null) {
      return String(s.label);
    }
    return this.getResStatusName(s) || '';
  }

  private formatStatementAmount(value: number | null | undefined): string {
    if (value == null || (typeof value === 'number' && isNaN(value))) {
      return '$0.00';
    }
    const n = Number(value);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(n));
    return n < 0 ? `(${formatted})` : formatted;
  }

  private getStatementAmountNumeric(value: number | null | undefined): number {
    if (value == null || (typeof value === 'number' && isNaN(value))) {
      return 0;
    }
    return Number(value);
  }

  private buildAndDownloadExcel(reservations: any[]): void {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Owner Statements', { views: [{ state: 'frozen', ySplit: 2 }] });

    const fillGreen = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF2E7D32' } };
    const fillBlue = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF007BFF' } };
    const fillYellow = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFFF7E0' } };
    const fillGray = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF6C757D' } };
    const fillGrey = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFE9ECEF' } };
    const fillPink = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFFEBEE' } };
    const fillLightBlue = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFE3F2FD' } };
    const fillDarkGrey = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFB0BEC5' } };
    const fillMismatch = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFFEBEE' } };
    const borderLeftThick = { left: { style: 'thick' as const } };

    // Row 1: Group headers — add row first so column A is not pushed out; then merge
    const groupHeaderValues = [
      '', '', '', '',                                       // 1-4: Month/Year, Owner Stmt Id, Property, Status
      'Nights', '', '', '', '', '',                         // 5-10
      'Balance', '',                                        // 11-12
      'Payments', '',                                       // 13-14
      'Revenue', '', '', '', '',                            // 15-19
      'Maintenance & Housekeeping', '', '',                 // 20-22
      'Expenses', '', '', '', '',                            // 23-27
      'Reconciliation', ''                                  // 28-29
    ];
    const groupHeaderRow = sheet.addRow(groupHeaderValues);
    sheet.mergeCells(1, 5, 1, 10);
    sheet.mergeCells(1, 11, 1, 12);
    sheet.mergeCells(1, 13, 1, 14);
    sheet.mergeCells(1, 15, 1, 19);
    sheet.mergeCells(1, 20, 1, 22);
    sheet.mergeCells(1, 23, 1, 27);
    sheet.mergeCells(1, 28, 1, 29);
    groupHeaderRow.font = { bold: true };
    groupHeaderRow.height = 22;
    groupHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
    groupHeaderRow.getCell(15).fill = fillGreen;
    groupHeaderRow.getCell(15).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    groupHeaderRow.getCell(20).fill = fillBlue;
    groupHeaderRow.getCell(20).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    groupHeaderRow.getCell(23).fill = fillYellow;
    groupHeaderRow.getCell(23).font = { bold: true };
    groupHeaderRow.getCell(23).border = borderLeftThick;
    groupHeaderRow.getCell(28).fill = fillGray;
    groupHeaderRow.getCell(28).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    groupHeaderRow.getCell(28).border = borderLeftThick;

    const subHeaders = [
      'Month/Year', 'Owner Stmt Id', 'Property', 'Status',
      'Total#', 'G#', 'O#', 'OG#', 'Maint#', 'Occ(%)',
      'Starting', 'Ending',
      'Net', 'To Owner',
      'Rents', 'Tax', 'Maint', 'Hskp', 'Total',
      'Total', 'KBM Paid', 'Owner Paid',
      'Maint', 'Hksp', 'Mgmt', 'Tax', 'Total',
      'Discrepancy', 'Match'
    ];
    const subHeaderRow = sheet.addRow(subHeaders);
    subHeaderRow.font = { bold: true };
    subHeaderRow.height = 22;
    subHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };

    subHeaderRow.getCell(6).fill = fillGrey;
    subHeaderRow.getCell(7).fill = fillPink;
    subHeaderRow.getCell(8).fill = fillLightBlue;
    subHeaderRow.getCell(9).fill = fillDarkGrey;
    subHeaderRow.getCell(13).border = borderLeftThick;
    subHeaderRow.getCell(15).fill = fillGreen;
    subHeaderRow.getCell(15).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    subHeaderRow.getCell(15).border = borderLeftThick;
    [16, 17, 18, 19].forEach((c) => {
      subHeaderRow.getCell(c).fill = fillGreen;
      subHeaderRow.getCell(c).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    subHeaderRow.getCell(20).fill = fillBlue;
    subHeaderRow.getCell(20).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    subHeaderRow.getCell(20).border = borderLeftThick;
    [21, 22].forEach((c) => {
      subHeaderRow.getCell(c).fill = fillBlue;
      subHeaderRow.getCell(c).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    subHeaderRow.getCell(23).fill = fillYellow;
    subHeaderRow.getCell(23).font = { bold: true };
    subHeaderRow.getCell(23).border = borderLeftThick;
    [24, 25, 26, 27].forEach((c) => {
      subHeaderRow.getCell(c).fill = fillYellow;
      subHeaderRow.getCell(c).font = { bold: true };
    });
    subHeaderRow.getCell(28).fill = fillGray;
    subHeaderRow.getCell(28).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    subHeaderRow.getCell(28).border = borderLeftThick;
    subHeaderRow.getCell(29).fill = fillGray;
    subHeaderRow.getCell(29).font = { bold: true, color: { argb: 'FFFFFFFF' } };


    const currencyColumns = [11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28];
    
    reservations.forEach((r) => {
      const occ = (r?.stmtSummeryAPI?.occupancyRate != null) ? `${Number(r.stmtSummeryAPI.occupancyRate).toFixed(0)} %` : '0 %';
      const matchStr = r?.stmtReconcile?.expenseMatch === true ? 'Yes' : r?.stmtReconcile?.expenseMatch === false ? 'No' : '—';
      const statusLabel = this.getStmtStatusLabelForExport(r);
      const row = [
        r?.startDate ? moment(r.startDate).format('MMM y') : '',
        r?.ownerStmtId ?? '',
        r?.propertyCode ?? '',
        statusLabel,
        (r?.stmtSummeryAPI?.paidNights ?? 0),
        (r?.guestNight ?? 0),
        (r?.ownerNights ?? 0),
        (r?.ownerGuestNights ?? 0),
        (r?.maintenanceNights ?? 0),
        occ,
        this.getStatementAmountNumeric(r?.startingBalance),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.endingBalance),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.totalRevenueWithExpenseCredit),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.paymentToOwner),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.rentalRevenue),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.taxRevenue),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.maintenanceCreditTotal),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.housekeepingCreditTotal),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.totalRevenue),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.maintenanceAndHousekeepingTotal),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.maintenanceAndHousekeepingPaidByKbmTotal),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.maintenanceAndHousekeepingPaidByOwnerTotal),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.maintenancePaidByOwnerTotal),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.housekeepingPaidByOwnerTotal),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.mgntExpense),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.taxExpense),
        this.getStatementAmountNumeric(r?.stmtSummeryAPI?.totalExpense),
        this.getStatementAmountNumeric(r?.stmtReconcile?.discrepancyAmount),
        matchStr
      ];
      const dataRow = sheet.addRow(row);
      
      currencyColumns.forEach((colIndex) => {
        const cell = dataRow.getCell(colIndex);
        const currentValue = cell.value;
        if (typeof currentValue === 'string') {
          const numValue = parseFloat(currentValue.toString().replace(/[^0-9.-]/g, '')) || 0;
          cell.value = numValue;
        } else if (currentValue != null) {
          cell.value = Number(currentValue);
        } else {
          cell.value = 0;
        }
        cell.numFmt = '$#,##0.00_);($#,##0.00)';
      });
      
      dataRow.getCell(13).border = borderLeftThick;
      dataRow.getCell(15).border = borderLeftThick;
      dataRow.getCell(20).border = borderLeftThick;
      dataRow.getCell(23).border = borderLeftThick;
      dataRow.getCell(28).border = borderLeftThick;
      if (r?.stmtReconcile?.expenseMatch === false) {
        dataRow.eachCell((cell) => {
          cell.fill = fillMismatch;
        });
      }
    });

    const lastDataRow = 2 + reservations.length;
    const lastColLetter = 'AC';
    if (lastDataRow > 2) {
      sheet.autoFilter = { from: 'A2', to: `${lastColLetter}${lastDataRow}` };
    }

    sheet.columns.forEach((col) => {
      let max = 12;
      col.eachCell?.({ includeEmpty: true }, (cell) => {
        const len = cell.value != null ? String(cell.value).length : 0;
        if (len > max) {
          max = len;
        }
      });
      col.width = Math.min(max + 1, 28);
    });
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `Owner_Statements_${moment().format('MM-DD-YYYY_HH-mm-ss')}.xlsx`;
      FileSaver.saveAs(blob, fileName);
    });
  }

  buildBulkUpdateForm() {
    this.bulkUpdateForm = this.formBuilder.group({
      statementMonth: [null, Validators.required],
      properties: [[]],
      oldStatus: [null, Validators.required],
      newStatus: [null, [Validators.required, this.sameStatusValidator.bind(this)]]
    });
    this.selectedMonthDate ? 
      this.bulkUpdateForm?.get('statementMonth')?.setValue(this.selectedMonthDate)
      : "";
    
    let properties = [];
    if(this.lazyLoadEvent?.filters?.['shortTermRentalId']?.value) {
      this.lazyLoadEvent?.filters?.['shortTermRentalId']?.value?.forEach((element: any) => {
        properties.push(element);
      });
    }
    this.bulkUpdateForm?.get('properties')?.setValue(properties);
    this.bulkUpdateForm.get('oldStatus')?.valueChanges.subscribe(() => {
      this.bulkUpdateForm.get('newStatus')?.updateValueAndValidity();
    });
  }

  sameStatusValidator(control: any): { [key: string]: any } | null {
    if (!this.bulkUpdateForm) {
      return null;
    }
    const oldStatus = this.bulkUpdateForm.get('oldStatus')?.value;
    const newStatus = control.value;
    if (oldStatus !== null && newStatus !== null && oldStatus === newStatus) {
      return { sameStatus: true };
    }
    return null;
  }

  openBulkUpdateDialog() {
    this.buildBulkUpdateForm();
    this.showBulkUpdateDialog = true;
  }

  isBulkUpdateFormValid(): boolean {
    if (!this.bulkUpdateForm) {
      return false;
    }
    
    Object.keys(this.bulkUpdateForm.controls).forEach(key => {
      this.bulkUpdateForm.get(key)?.markAsTouched();
    });

    return this.bulkUpdateForm.valid;
  }

  confirmBulkUpdate() {
    if (!this.isBulkUpdateFormValid()) {
      return;
    }

    const formValue = this.bulkUpdateForm.value;

    const selectedDate = moment(formValue.statementMonth);
    const startDateFormatted = selectedDate.format('YYYY-MM-01');

    const bulkUpdatePayload = {
      startDate: startDateFormatted,
      oldStatus: formValue.oldStatus,
      newStatus: formValue.newStatus,
      shortTermRentalIds: formValue.properties && formValue.properties.length > 0 ? formValue.properties : []
    };

    this.loading = true;
    this.ownerStmtService.bulkUpdateStatus(bulkUpdatePayload).subscribe(
      (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Bulk update completed successfully.',
          life: 3000
        });

        this.loadLazy(this.lazyLoadEvent);
        
        this.showBulkUpdateDialog = false;
        this.bulkUpdateForm.reset();

        this.loading = false;
      },
      (err: any) => {
        // Do NOT reset loading on error — a 504 means the bulk update may have already
        // completed on the server. Keep spinner/button locked to block re-submit.
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Bulk update may have completed. Please close this dialog and refresh before retrying.',
          life: 7000
        });
        console.log('confirmBulkUpdate FAIL: ' + JSON.stringify(err?.error?.message || err?.message));
      }
    );
  }


  openBulkMailDialog() {
    if (this.selectedStatements.size === 0) {
      this.messageService.add({ severity: 'warn', summary: 'No Statements Selected', detail: 'Select statements using the checkboxes before using Bulk Mail.', life: 5000 });
      return;
    }
    this.bulkMailMonthYear = this.selectedMonthDate ? new Date(this.selectedMonthDate) : new Date();
    this.bulkMailPassword = '';
    this.bulkMailError = '';
    this.bulkMailProgress = '';
    this.loadChecklistFromStorage();
    this.showBulkMailDialog = true;
  }

  get bulkMailChecklistComplete(): boolean {
    return this.checklistCompletedCount === this.checklistTotalCount;
  }

  confirmBulkMail() {
    this.bulkMailError = '';
    if (!this.bulkMailChecklistComplete) {
      this.bulkMailError = 'Please complete every checklist item before sending.';
      return;
    }
    if (this.selectedStatements.size === 0) {
      this.bulkMailError = 'No statements selected.';
      return;
    }
    if (this.bulkMailPassword !== '@lohaKBM') {
      this.bulkMailError = 'Confirmation phrase incorrect.';
      return;
    }
    const ownerStmtIds = Array.from(this.selectedStatements);
    this.bulkMailLoading = true;
    this.bulkMailProgress = `Sending ${ownerStmtIds.length} selected statement(s)...`;

    // Build audit info — who confirmed, when, which period, full checklist snapshot
    const currentUser = this.authenticationService.currentUserValue;
    const confirmedByUsername = currentUser?.username || '';
    const confirmedByName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ');
    const confirmedAt = new Date().toISOString();
    const periodDate = this.bulkMailMonthYear || this.selectedMonthDate;
    const statementPeriod = periodDate
      ? moment(periodDate).format('MMM YYYY')
      : '';
    const checklistItems: Record<string, boolean> = { ...this.bulkMailChecklist };
    const auditInfo = { confirmedByUsername, confirmedByName, confirmedAt, statementPeriod, checklistItems };

    this.ownerStmtService.bulkMailStatements(ownerStmtIds, this.bulkMailPassword, auditInfo).subscribe(
      (res: any) => {
        this.bulkMailLoading = false;
        const data = res?.data || {};
        const sent = data.sent ?? 0;
        const failed = data.failed ?? 0;
        const skipped = data.skipped ?? 0;
        const skippedMsg = skipped > 0 ? `, Skipped (not Published or already sent): ${skipped}` : '';
        this.messageService.add({
          severity: failed > 0 ? 'warn' : 'success',
          summary: 'Bulk Mail Complete',
          detail: `Sent: ${sent}, Failed: ${failed}${skippedMsg}`,
          life: 7000
        });
        this.showBulkMailDialog = false;
        this.bulkMailPassword = '';
        this.clearSelection();
        this.loadLazy(this.lazyLoadEvent);
      },
      (err: any) => {
        // Do NOT reset bulkMailLoading on error — a 504 means the bulk mail may have already
        // been sent by the server. Keep button disabled to block re-send.
        this.bulkMailError = 'Bulk mail may have been sent. Please refresh before retrying.';
        console.log('confirmBulkMail FAIL: ' + JSON.stringify(err?.error?.message || err?.message));
      }
    );
  }

  sendStatementEmail(event: Event, statement: { ownerStmtId: number; propertyCode?: string; startDate?: any }) {
    event.stopPropagation();
    event.preventDefault();
    try {
      this.ownerStatementMailService.openStatementMailCompose(statement, {
        onLoading: (v) => this.mailComposeLoading = v,
        onPublishRequired: (summary, detail) => this.messageService.add({ severity: 'error', summary, detail, life: 5000 })
      }).subscribe((sent) => {
        if (sent) {
          this.loadLazy(this.lazyLoadEvent);
        }
      });
    } catch (error) {
      console.error('Error preparing statement for email:', error);
      this.alertService?.error('Failed to prepare statement for email. Please try again.');
    }
  }


  viewExpenses() {
    if (!this.selectedStatementRow) return;
    this.openExpenseList(null, this.selectedStatementRow);
  }

  openDeleteStatementConfirm() {
    if (!this.selectedStatementRow) return;
    this.deleteCodeValue = '';
    this.deleteCodeError = '';
    this.showDeleteConfirmDialog = true;
  }

  cancelDeleteConfirm() {
    this.showDeleteConfirmDialog = false;
    this.deleteCodeValue = '';
    this.deleteCodeError = '';
  }

  confirmDeleteStatement() {
    this.deleteCodeError = '';
    const code = (this.deleteCodeValue || '').trim();
    if (code.toLowerCase() !== 'delete') {
      this.deleteCodeError = 'Code does not match';
      return;
    }
    const ownerStmtId = this.selectedStatementRow?.ownerStmtId;
    if (ownerStmtId == null) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Statement ID is missing.', life: 5000 });
      return;
    }
    this.deleteStatementLoading = true;
    this.ownerStmtService.deleteOwnerStatement(ownerStmtId).pipe(first()).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Statement deleted.', life: 3000 });
        this.cancelDeleteConfirm();
        this.loadLazy(this.lazyLoadEvent || { first: 0, rows: 300 });
      },
      error: (err) => {
        this.deleteStatementLoading = false;
        this.alertService.error(err);
      },
      complete: () => {
        this.deleteStatementLoading = false;
      }
    });
  }

  openExpenseList(event: Event | null, reservation: any): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const startDate = reservation?.startDate ? moment(reservation.startDate).format('YYYY-MM-DD') : null;
    const endDate = reservation?.endDate
      ? moment(reservation.endDate).format('YYYY-MM-DD')
      : (reservation?.startDate ? moment(reservation.startDate).endOf('month').format('YYYY-MM-DD') : null);
    const queryParams: any = {};
    if (reservation?.shortTermRentalId) {
      queryParams.shortTermRentalId = reservation.shortTermRentalId;
    }
    if (startDate) {
      queryParams.startDate = startDate;
    }
    if (endDate) {
      queryParams.endDate = endDate;
    }
    if (reservation?.propertyCode) {
      queryParams.propertyCode = reservation.propertyCode;
    }
    // queryParams.showToStmt = true;
    queryParams.forReconciliation = true;
    const queryString = Object.keys(queryParams)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&');
    const url = `/expense-list${queryString ? '?' + queryString : ''}`;
    window.open(url, '_blank');
  }


  openTopicMessages(event: Event | null, reservation: any): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if(reservation?.topicMsgStats?.messageCount === 0) {
      this.messageService.add({ severity: 'error', summary: 'No mails sent', detail: 'No mails sent for this statement.', life: 3000 });
      return;
    }
    const ownerStmtId = reservation?.ownerStmtId;
    if (ownerStmtId == null) {
      this.messageService.add({ severity: 'warn', summary: 'No statement', detail: 'Statement ID is missing.', life: 3000 });
      return;
    }
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '100vh';
    dialogConfig.panelClass = 'topic-messages-fullpage-dialog';
    dialogConfig.data = {
      topic: OwnerStatementsComponent.TOPIC_STATEMENT,
      topicId: ownerStmtId,
      title: 'Statement Messages'
    };
    this.dialog.open(TopicMessagesViewComponent, dialogConfig);
  }


  viewSentbox() {
    const queryParams = {
      status: 2,
      topic: 2600
    };
    if (this.selectedStatementRow?.ownerStmtId != null) {
      queryParams['statementId'] = this.selectedStatementRow.ownerStmtId;
      queryParams['topicId'] = this.selectedStatementRow.ownerStmtId;
    }
    if (this.selectedStatementRow?.propertyProfileId != null) {
      queryParams['propertyProfileId'] = this.selectedStatementRow.propertyProfileId;
    }
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/inbox-list-vertical', '8'], { queryParams })
    );
    window.open(url, '_blank');
  }

  isStatementSelected(ownerStmtId: number): boolean {
    return this.selectedStatements.has(ownerStmtId);
  }

  toggleStatementSelection(ownerStmtId: number) {
    if (this.selectedStatements.has(ownerStmtId)) {
      this.selectedStatements.delete(ownerStmtId);
    } else {
      this.selectedStatements.add(ownerStmtId);
    }
    this.updateSelectAllState();
  }

  toggleSelectAll() {
    if (this.selectAllChecked) {
      // Deselect all visible
      this.reservations?.forEach(r => {
        if (r?.ownerStmtId != null) {
          this.selectedStatements.delete(r.ownerStmtId);
        }
      });
      this.selectAllChecked = false;
    } else {
      // Select all visible
      this.reservations?.forEach(r => {
        if (r?.ownerStmtId != null) {
          this.selectedStatements.add(r.ownerStmtId);
        }
      });
      this.selectAllChecked = true;
    }
  }

  private updateSelectAllState() {
    if (!this.reservations || this.reservations.length === 0) {
      this.selectAllChecked = false;
      return;
    }
    this.selectAllChecked = this.reservations.every(r => r?.ownerStmtId != null && this.selectedStatements.has(r.ownerStmtId));
  }

  get selectedCount(): number {
    return this.selectedStatements.size;
  }

  get selectedPublishedCount(): number {
    if (!this.reservations || this.selectedStatements.size === 0) return 0;
    let count = 0;
    this.reservations.forEach(r => {
      if (r?.ownerStmtId != null && this.selectedStatements.has(r.ownerStmtId) && r?.stmtStatus === 7) {
        count++;
      }
    });
    return count;
  }

  clearSelection() {
    this.selectedStatements.clear();
    this.selectAllChecked = false;
  }

}
